import { useMutation } from "@apollo/client";
import { TextField } from "@mui/material";
import { Stack } from "@mui/system";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { DocumentFilePicker } from "components/Document/shared/DocumentFilePicker";
import { DocumentType } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useDocumentUpload } from "hooks/useDocumentUpload";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import { useCallback, useState } from "react";

import {
  ATTACH_DOCUMENT_TO_MANIFEST,
  MARK_TRANSPORTER_CONTACTED,
  PROCESSED_MANIFEST_DOCUMENTS_QUERY,
  PROCESSED_MANIFESTS_QUERY,
} from "./api";
import {
  BULK_MANIFEST_DISCREPANCIES_QUERY,
  COMPLIANCE_MANIFEST_DATA_QUERY,
} from "./DiscrepancyReviewDialog";

interface ProofOfContactDialogProps {
  open: boolean;
  onClose: () => void;
  processedManifestId: string;
}

export function ProofOfContactDialog({
  open,
  onClose,
  processedManifestId,
}: ProofOfContactDialogProps) {
  const { user } = useCurrentUser();
  const { tenantId } = useTenant();
  const alerts = useAlerts();

  const defaultName = [user?.person?.first, user?.person?.last].filter(Boolean).join(" ");
  const [contactedBy, setContactedBy] = useState(defaultName);
  const [contactedAt, setContactedAt] = useState<DateTime | null>(DateTime.now());
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const [attachDocumentToManifest] = useMutation(ATTACH_DOCUMENT_TO_MANIFEST, {
    refetchQueries: [PROCESSED_MANIFEST_DOCUMENTS_QUERY],
  });
  const [markContacted] = useMutation(MARK_TRANSPORTER_CONTACTED, {
    refetchQueries: [
      PROCESSED_MANIFESTS_QUERY,
      COMPLIANCE_MANIFEST_DATA_QUERY,
      BULK_MANIFEST_DISCREPANCIES_QUERY,
    ],
  });

  const { uploadFile } = useDocumentUpload({
    refetchQueries: [PROCESSED_MANIFEST_DOCUMENTS_QUERY],
    onSuccess: async (document) => {
      try {
        await attachDocumentToManifest({
          variables: {
            processedManifestId,
            documentId: document.id,
          },
        });
        await markContacted({
          variables: {
            processedManifestId,
            contactedBy: contactedBy.trim(),
            contactedAt: contactedAt?.toISO() ?? "",
          },
        });
        alerts.success("Proof of contact uploaded");
        onClose();
      } catch {
        alerts.error("Failed to save proof of contact");
      } finally {
        setSubmitting(false);
      }
    },
    onError: () => {
      alerts.error("Failed to upload document");
      setSubmitting(false);
    },
  });

  const handleSubmit = useCallback(async () => {
    if (!contactedBy.trim() || !contactedAt || files.length === 0 || !tenantId) return;

    setSubmitting(true);
    const file = files[0];
    const fileExtension = file.name.split(".").pop() ?? "";

    await uploadFile({
      file,
      documentData: {
        tenantId,
        title: `Transporter Contact Proof — ${file.name}`,
        documentType: DocumentType.WasteTransporterContactProof,
        fileExtension,
      },
      currentSubmissionDocument: false,
    });
  }, [contactedBy, contactedAt, files, tenantId, uploadFile]);

  const isValid = contactedBy.trim().length > 0 && contactedAt != null && files.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title="Add Proof of Contact"
      primaryActionButtonProps={{
        saveText: "Upload",
        loadingText: "Uploading...",
        loading: submitting,
        onClick: handleSubmit,
        disabled: !isValid || submitting,
      }}
    >
      <Stack spacing={3}>
        <TextField
          label="Contacted by"
          value={contactedBy}
          onChange={(e) => setContactedBy(e.target.value)}
          fullWidth
          required
        />
        <DatePicker
          label="Date of contact"
          value={contactedAt}
          onChange={setContactedAt}
          slotProps={{
            textField: { fullWidth: true, required: true },
          }}
        />
        <DocumentFilePicker
          files={files}
          onDrop={(dropped) => setFiles(dropped.slice(0, 1))}
          onRemove={() => setFiles([])}
          canAddMore={files.length === 0}
          maxFiles={1}
          helperText="Upload proof of transporter contact (e.g. email, letter)"
        />
      </Stack>
    </Dialog>
  );
}
